import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {BuilderHistoryService} from "./builder-history.service";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {DbModalService} from "../modules/modal/modal";
import {BuilderStepsEnum, BuilderTourEnum} from "./builder-tour.enum";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SUPPORTED_FONTS_LIST} from "./components/website-customizer/supported-fonts-list.constants";
import {filter, first, map, mergeMap, switchMap, tap} from "rxjs/operators";
import {combineLatest, Observable, of} from "rxjs";
import {ActivityAction, ActivityStatus} from "../activities/activity";
import {ActivityNotificationService} from "../activity-notifications/activity-notification.service";
import {BuilderV2Service} from "./builder-v2.service";
import {
  BuilderGeneratedPageI,
  BuilderGeneratedPageSectionI, CombinedOutlineI, CombinedOutlineRespI,
  LogoParamsI,
  PageMetaI,
  PageThemeI,
  ProductCategoriesI,
  SiteGenerationI,
  SiteGenerationMobileI, SiteGenerationVibeCodingI,
  StorageSiteStateI
} from "./builder-v2.interface";
import {WEBSITE_CUSTOMIZATIONS} from "./components/website-customizer/wesite-customization-options.constants";
import {BuilderSectionsInteractionService} from "./builder-sections-interaction.service";
import * as fromStore from '../store';
import {DbStoreState} from '../store';
import {Store} from "@ngrx/store";
import {DialogService} from "../../ng2-bootstrap-modal/dialog.service";
import {
  NotificationDialogComponent
} from "../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {NotifierComponent} from "../shared/components/notifier/notifier.component";
import {Notification} from "../shared/components/notifier/notification";
import {NotificationType} from "../shared/components/notifier/notification-type";
import {showConfetti} from "../shared/utils/confetti";
import {DbActiveModal, DbModalRef} from "../modules/modal/modal-ref";
import {GoogleAnalyticsService} from "../shared/services/google-analytics/google-analytics.service";
import {Config} from "../config/config";
import {User} from "../user/user";
import {ActivatedRoute, Router} from "@angular/router";
import {CookieService} from "ngx-cookie";
import {
  CreateAiAssistantSignUpComponent
} from "../components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-sign-up/create-ai-assistant-sign-up.component";
import {TextUtils} from "../shared/utils/text-utils";
import aiSteps from "./builder-steps.json";
import {CommonBuilderDataService} from "./services/common-builder-data.service";
import {TriggersService} from "./services/triggers.service";
import {CanvasZoomService} from "./services/canvas-zoom.service";
import * as LZString from 'lz-string';
import {AuthService} from "../auth/auth.service";
import {SinglePageService} from "./single-page/services/single-page.service";
import {DashboardUtils} from "../shared/utils/dashboard-utils";
import {SuccessMobileDialogComponent} from "./components/success-mobile-dialog/success-mobile-dialog.component";
import builderLimits from "./builder-limits.json";
import {Location} from "@angular/common";
import {DbResponse} from "../shared/interceptors/db-response";
import {ReCaptchaV3Service} from "ngx-captcha";
import {SignupShortFlowComponent} from "../components/pages/signup-short-flow/signup-short-flow.component";
import {Utils} from "../shared/utils/utils";
import {GlobalEvent} from "../shared/services/global-event-dispatcher/global-event";
import {GlobalEventDispatcherService} from "../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {SignUpFor} from "../add-website/typeFormAnswers";
import {UserService} from "../user/user.service";
import {UserSubscription} from "../user/user-subscription";
import {HostingService} from "../websites/website/hosting/hosting.service";
import {
  HostedTrialStartedComponent
} from "../shared/components/dialogs/account-upgraded-dialog/hosted-trial-started/hosted-trial-started.component";
import {TypeUtils} from "../shared/utils/type-utils";
import {SecondaryPagesService} from "./services/secondary-pages.service";
import {VibeCodingFlowService} from "../shared/services/vibe-coding-flow.service";
import {Website} from "../websites/website/website";

@Component({
  selector: 'db-builder-v2',
  templateUrl: './builder-v2.component.html',
  styleUrl: './builder-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BuilderV2Component implements OnInit, OnDestroy, AfterViewInit {

  @Input() popupMode = false;
  @Input() fromVibeCoding = false;
  @Input() emailCampaign = false;
  @Input() companyType: string;
  @Output() buildComplete = new EventEmitter<void>();
  @Input() domainId = this._route.parent?.parent?.snapshot?.data['website']?.id;
  @Input() demoUser: boolean = false;
  @Input() currentOutline = false;
  @Input() isOnboarding = true;
  @Input() fromWebsiteCreationPrompt = true;
  @Input() isFromGenerate: boolean;
  @Input() forRecreateFirstTime: boolean;
  @Input() user: User;
  @Input() logo: any = 'assets/images/logos/logo_placeholder.png';
  @Input() builderType: 'wvc' | 'section_based';
  @Input() isMobileAutoGenerate: boolean = false;
  @Input() triggerDescriptionFlow: {
    description: string;
    industryName?: string;
    websiteType?: string;
    images?: any[];
    figmaUrl?: string;
    uniqueId?: string;
    // Pre-generated data (if provided, skip API calls)
    enhancedDescription?: string;
    businessName?: string;
    businessType?: string;
    outline?: any;
    styles?: any;
  };
  websiteInfo;
  isSinglePageMode = this._route.snapshot.data['isSinglePage'];
  prefilledAi = false;
  prefilledAiData: any;
  couponPercent: any;

  scale: number = 1;
  fontStyles: SafeHtml[] = [];
  tourEnum = BuilderTourEnum;
  tourStep = null;
  generationStarted: boolean = false;
  accentZIndex = 20;
  requestActions = {
    [ActivityAction.BUILDER_GET_OUTLINE]: this._handleOutlineResp.bind(this),
    [ActivityAction.BUILDER_STYLES]: this._handleStyleResp.bind(this),
    [ActivityAction.PRODUCTS_GENERATE]: this._handleProductCategories.bind(this),
    [ActivityAction.BUILDER_SECTION_TYPES]: this._handleModifySectionResp.bind(this),
    [ActivityAction.BUILDER_SECTION_ENHANCE]: this._handleEnhanceResp.bind(this),
    // [ActivityAction.BUILDER_BUILD_SITE_OUTLINE]: this._handleBuildSiteComplete.bind(this),
    [ActivityAction.INSTALL_HOSTED_TEMPLATE]: this._handleBuildSiteComplete.bind(this),
    [ActivityAction.BUILDER_SECTION_GENERATE]: this._handleEnhanceResp.bind(this),
    [ActivityAction.BUILDER_SECTION_SUGGEST_TITLE]: this._handleEnhanceResp.bind(this),
    [ActivityAction.BUILDER_SECTION_SUGGEST_DESCRIPTION]: this._handleEnhanceResp.bind(this),
    [ActivityAction.BUILDER_COLORS]: this._handleModifyColorResp.bind(this),
    [ActivityAction.CREATE_WEBSITE]: this._handleBuildSiteVibe.bind(this),
    [ActivityAction.BUILDER_BUILD_SITE_DESC]: this._handleBuildSiteComplete.bind(this),
    [ActivityAction.BUILDER_SECONDARY_PAGE]: this._handleBuildSecondaryPage.bind(this),
  };
  outline: BuilderGeneratedPageI;
  pageTheme: PageThemeI;
  fromStartAIPage: boolean;
  aiSteps: string[] = aiSteps;
  uniqueId: string;
  builderStep = BuilderStepsEnum;
  fromInnerAIPage: boolean;
  isMobile: boolean;
  private isFromTriggerDescriptionFlow: boolean = false;
  openMagicLink: boolean;
  userLocalStorage: any;
  aiColorsSet: boolean = false;
  params: any;
  pageMetas: PageMetaI[] = [];
  @Input() website?: Website = this._route.parent?.parent?.snapshot?.data['website'];
  storageData: any;
  isEcommerce: boolean = false;
  shortFlow: boolean;
  outlineResponse: any;
  styleResponse: any;
  popupOpened: boolean = false;
  campaignInfo: any;
  vibeCodingFlow: any;
  cookieData: any;
  @Input() userSubscription?: UserSubscription;
  private _generationModalRef: DbModalRef;
  private _destroyRef = inject(DestroyRef);
  private _alreadySubscribedToNotifs = false;
  private _buildCompleteDialogShown = false;
  private isTier1Country: boolean = false;
  private _hasWhitelisted = false;
  customPostTypes = [];


  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.classList.contains('back-generation-step') && this.popupMode) {
      this._activeModal.close();
    }
    if (target.classList.contains('modal-close-outside')) {
      this._modalService.dismissAll();
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.which === 9 && event.shiftKey) {
      event.preventDefault();
    }
  }

  constructor(public builderHistoryService: BuilderHistoryService,
              private _modalService: DbModalService,
              private _activeModal: DbActiveModal,
              private _builderService: BuilderV2Service,
              private _route: ActivatedRoute,
              private _store: Store<DbStoreState>,
              private _activityNotificationService: ActivityNotificationService,
              public builderInteractionsService: BuilderSectionsInteractionService,
              private sanitizer: DomSanitizer,
              private reCaptchaV3Service: ReCaptchaV3Service,
              private _dialogService: DialogService,
              private _cookieService: CookieService,
              private _activeRoute: ActivatedRoute,
              private _googleAnalyticsService: GoogleAnalyticsService,
              private _commonDataService: CommonBuilderDataService,
              private _builderTriggerService: TriggersService,
              private _cdr: ChangeDetectorRef,
              private _authService: AuthService,
              private _userService: UserService,
              private _singePageService: SinglePageService,
              private _router: Router,
              private _location: Location,
              private elRef: ElementRef,
              private renderer: Renderer2,
              private _globalEventDispatcher: GlobalEventDispatcherService,
              private _hostingService: HostingService,
              private _triggerService: TriggersService,
              private _canvasZoomService: CanvasZoomService,
              private _secondaryPagesService: SecondaryPagesService,
              private _vibeCodingFlowService: VibeCodingFlowService
  ) {
  }

  ngOnInit() {

    this._setIsTier1();
    this.isMobile = window.innerWidth <= 991;
    !this.user && (this.user = this._route.snapshot.parent?.parent?.data['user']);
    !this.userSubscription && (this.userSubscription = this._route.snapshot.parent?.parent?.data['userSubscription']);
    this.fromInnerAIPage = window.location.href.indexOf("ai-builder") > -1;

    // Set prefilledAi early if triggerDescriptionFlow is provided
    if (this.triggerDescriptionFlow) {
      /*this.prefilledAi = true;*/
      /*this._commonDataService.prefilledAi = true;*/
      this._canvasZoomService.scale.next(0.8);
    }

    // Set builderType in common data service for vibe coding flow detection
    // Use @Input builderType if provided, otherwise use website builderType
    this._commonDataService.builderType = this.builderType || this.website?.builderType || null;

    this.vibeCodingFlow = this._vibeCodingFlowService.isVibeCodingFlow();
    this._vibeCodingFlowService.isCombinedOutline();
    if (this.isMobileAutoGenerate || this.isMobile) {
      this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_FINALIZE);
    }


    if (sessionStorage.getItem('sessionId')) {
      this.uniqueId = sessionStorage.getItem('sessionId');
      this._commonDataService.uniqueId = this.uniqueId;
      // UniqueId is ready, proceed with flow
      this._proceedWithFlow();
    } else {
      this._getUniqueId();
    }
    this._initFunctions();
    this._listenAnimationDone();
    this._listenDialogFreshOpen();
    this._listenSectionsLoadedForFreshOpen();
    this._loadWebsites();
    this.getUser();
    const fromGoogleSignup = window.location.href.indexOf("finalize") > -1 || localStorage.getItem('dbShowV2Finalize');
    const fromMobileSignup = window.location.href.indexOf("showFinalize") > -1;
    this.shortFlow = window.location.href.indexOf("shortFlow") > -1;
    this.fromStartAIPage = window.location.href.indexOf("v2-ai-website-building") > -1;
    this.campaignInfo = this._userService.getCampaignInfo();

    this.builderHistoryService.getLocalStorage();
    if (!this.isSinglePageMode) {
      this.showFinalize(fromGoogleSignup, fromMobileSignup);
    }
    this._commonDataService.domainId = this.domainId;
    // Only call _setBuilderStep if we're not in trigger description flow
    // (trigger description flow already sets the step to SHOW_CANVAS)
    if (!this.isFromTriggerDescriptionFlow) {
      this._setBuilderStep();
    }
  }

  private _loadWebsites(): void {
    this._store
      .select(fromStore.getWebsitesLoaded)
      .pipe(
        filter((loaded: boolean): boolean => !!loaded),
        switchMap(() =>
          this._store.select(fromStore.getWebsites)
        )
      )
      .subscribe((websites) => {
        if (websites?.length && (this.userSubscription?.freeBaseClient || this.userSubscription?.trialClient)) {
          this.website = websites[0];
          if (!this._hasWhitelisted && !!this.isOnboarding) {
            // Claim the flag synchronously - this stream re-emits on every LoadWebsites, and
            // setting it only after the request resolved let duplicates through
            this._hasWhitelisted = true;
            this._hostingService.whiteListIp(this.website?.id, this.website?.site_url);
            this.builderInteractionsService.saveBrandKitData(this.website.id);
          }
          return;
        }

      });
  }

  private _pluginsLoaded(): void {
    this._store.select(fromStore.getWebsitePluginsLoaded(this.websiteInfo?.id))
      .pipe(
        first(loaded => loaded, null),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getWebsiteElementorPlugin(this.websiteInfo?.id)),
          this._store.select(fromStore.getWebsiteBuilderPlugin(this.websiteInfo?.id))
        )),
        first()
      )
      .subscribe((plugins) => {
        plugins.forEach(plugin => {
          this._pluginUpdate(plugin, this.websiteInfo);
        });
      });
  }

  private _pluginUpdate(product, website): void {
    if (product && product.updateAvailable) {
      this._store.dispatch(new fromStore.InitWebsiteProductAction({
        website: website,
        products: [product],
        action: ActivityAction.UPDATE,
        network: 1
      }));
    }
  }

  ngAfterViewInit() {
    const inputs = this.elRef.nativeElement.querySelectorAll('input, textarea');

    inputs.forEach((input: HTMLElement) => {
      // Disable zoom on the first focus
      this.renderer.listen(input, 'focus', () => this.disableZoom());
      this.renderer.listen(input, 'touchstart', () => this.disableZoom());
    });
  }

  disableZoom() {
    const viewport = document.querySelector("meta[name=viewport]");
    if (viewport) {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
      );
    }
  }

  private _initFunctions(): void {
    this._listenPageMetas();
    this._listenBuilderNotifications();
    this._listenSectionChange();
    this._listenChangeInState();
    this._initOutline();
    this._initSiteType();
    this._listenTour();
    this._listenEnhance();
    this.loadFontStyles();
  }

  private _listenAnimationDone() {
    this.builderInteractionsService.enhancedDescriptionIsDone
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(value => {
        if (value && !this.pageMetas.length) {
          this.builderHistoryService.tourStep.next(BuilderTourEnum.START);
          // Skip SHOW_CANVAS for mobile auto-generate
          if (!this.isMobileAutoGenerate && !this.isMobile) {
            this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);
          }
          this.builderInteractionsService.disableNextButton.next(false);
          if (!this._vibeCodingFlowService.isCombinedOutline()) {
            this._builderTriggerService.getPageMetas.next();
          }
          this._waitForLogosAndProceedToOutline();
        }
      });
  }

  private _listenDialogFreshOpen() {
    this.builderInteractionsService.prepareDialogForFreshOpen
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(value => value === true)
      )
      .subscribe(() => {
        // Set loading states to hide sidebar until outline is ready
        /*this.builderInteractionsService.generatePrefilledSiteLoading.next(true);
        this.builderInteractionsService.showSectionsLoading.next(true);*/

        // Reset canvas zoom for smooth zoom animation on dialog open
        this._canvasZoomService.scale.next(0.8);
        this.builderInteractionsService.prepareDialogForFreshOpen.next(false);
      });
  }

  private _listenSectionsLoadedForFreshOpen() {
    // When sections are loaded, hide the loading overlay for trigger description flow
    this.builderInteractionsService.secondaryPagesLoaded
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(value => value === true && this.isFromTriggerDescriptionFlow)
      )
      .subscribe(() => {
        this.builderInteractionsService.generatePrefilledSiteLoading.next(false);
      });
  }

  private _waitForLogosAndProceedToOutline() {
    const currentBrandKitOptions = this.builderInteractionsService.brandKitOptions.value as any;
    if (currentBrandKitOptions.logos?.length > 0 && currentBrandKitOptions.logos[0]?.icon_url) {
      this._proceedToOutline();
      return;
    }

    // Wait for actual logos to arrive
    const brandKitSubscription = this.builderInteractionsService.brandKitOptions.subscribe(options => {
      const optionsAny = options as any;
      if (optionsAny.logos?.length > 0 && optionsAny.logos[0]?.icon_url) {
        brandKitSubscription.unsubscribe();
        this._proceedToOutline();
      }
    });
    setTimeout(() => {
      brandKitSubscription.unsubscribe();
      this._proceedToOutline();
    }, 10000);
  }

  private _proceedToOutline() {
    if (this.outlineResponse) {
      setTimeout(() => {
        this.sendGeneratedOutline();
      }, 1000);
    }
  }

  private _setIsTier1() {
    // const getExperimentalCookie = this._cookieService.get('db-experiment');
    // this.isTier1Country = getExperimentalCookie ? JSON.parse(getExperimentalCookie) && JSON.parse(getExperimentalCookie).t1PExp && JSON.parse(getExperimentalCookie).t1PExp === 'b' : false;
    this.isTier1Country = this.builderHistoryService.isTier1Country;
    this._builderService.isTier1Country = this.builderHistoryService.isTier1Country;
  }

  sendGeneratedOutline(onlyOutline = false) {
    this.builderInteractionsService.requestSubject.next({
      action: ActivityAction.BUILDER_GET_OUTLINE,
      response: this.outlineResponse
    });

    if (!onlyOutline) {
      this.builderInteractionsService.requestSubject.next({
        action: ActivityAction.BUILDER_STYLES,
        response: this.styleResponse
      });
    }
  }


  private _listenPageMetas() {
    this._builderTriggerService.getPageMetas
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._getPageMeta();
      });
  }

  private _getSeoMetaData() {
    const {domain_name, analysis, ...siteInfo} = this.builderInteractionsService.siteInfo.value;
    const data = {
      ecom_data: this.builderInteractionsService.updateProductCategories.value,
      ...siteInfo
    };
    const {initial_description: _, ...outlineFormValue} = data;

    this._builderService.getSEOMetaData(this.domainId, this.uniqueId, outlineFormValue).pipe(filter(resp => !!resp?.data)).subscribe(({data}) => {
      this.builderInteractionsService.websiteInfo = {
        website_description: data?.website_description,
        website_keyphrase: data?.website_keyphrase,
        website_title: data?.website_title,
        website_design_type_level: data?.website_design_type_level
      };
    });
  }

  generateOutlineRequest() {
    this.builderInteractionsService.loading.next(true);
    const {
      business_name,
      industry_name,
      initial_description,
      enhanced_description,
      site_description,
      website_type,
      analysis,
      images,
      figma_url
    } = this.prefilledAiData;
    const formValue = {
      'business_description': enhanced_description || site_description,
      'business_type': industry_name,
      business_name,
      website_type,
      initial_description,
    };
    if (analysis && this._vibeCodingFlowService.isCombinedOutline()) {
      formValue['analysis'] = analysis;
    }
    // Add ecom_data for ecommerce websites
    if (website_type === 'ecommerce') {
      formValue['ecom_data'] = this.builderInteractionsService.updateProductCategories.value;
    }
    const {initial_description: _, ...outlineFormValue} = formValue;
    const _data = {
      ...formValue,
      analysis,
      domain_name: ''
    };
    this._vibeCodingFlowService.isCombinedOutline() && images && (_data['images'] = images);
    this._vibeCodingFlowService.isCombinedOutline() && figma_url && (_data['figma_url'] = figma_url);
    this.builderInteractionsService.siteInfo.next(_data);
    if (this.vibeCodingFlow && this.isMobile) {
      this._builderTriggerService.getPageMetas.next();
    }
    if (this._vibeCodingFlowService.isCombinedOutline()) {
      this._commonDataService.combinedOutline = true;
      this._builderService.aiGenerateOutline(outlineFormValue, this.domainId, this.uniqueId, false)
        .pipe(tap(value => {
          if (value) {
            const vibeCodingFlow = this._vibeCodingFlowService.isVibeCodingFlow() || this.website?.isWVC;

            if (vibeCodingFlow && this.isMobile) {
              this._handleOutlineResp(value.data);
            }
          }
        })).subscribe((outline) => {
        if ((outline && !outline.success)) {
          NotifierComponent.notify(new Notification(outline?.message, NotificationType.DANGER, true));
        }
        if (outline?.data) {
          if ((this.builderInteractionsService.enhancedDescriptionIsDone.value) && !this.outlineResponse) {
            setTimeout(() => {
              this.sendGeneratedOutline(true);
            }, 1000);
          }
          this.outlineResponse = outline;
        }
      });
      return;
    }
    this._commonDataService.combinedOutline = false;
    combineLatest([
      this._builderService.aiGenerateOutline(outlineFormValue, this.domainId, this.uniqueId, false)
        .pipe(tap(value => {
          if (value) {
            const vibeCodingFlow = this._vibeCodingFlowService.isVibeCodingFlow() || this.website?.isWVC;

            if (vibeCodingFlow && this.isMobile) {
              this._handleOutlineResp(value.data);
            }
          }
        })),
      this._builderService.aiGenerateStyles(outlineFormValue, this.domainId, this.uniqueId, false)
    ]).subscribe(([outline, styles]) => {
      if ((styles && !styles.success) || (outline && !outline.success)) {
        NotifierComponent.notify(new Notification(outline?.message || styles?.message, NotificationType.DANGER, true));
      }
      if (styles?.data && outline?.data) {
        if ((this.builderInteractionsService.enhancedDescriptionIsDone.value) && !this.outlineResponse) {
          setTimeout(() => {
            this.sendGeneratedOutline();
          }, 1000);
        }
        this.outlineResponse = outline;
        this.styleResponse = styles;
      }
    });
  }


  private _handlePreGeneratedData(data: {
    description: string;
    enhancedDescription: string;
    businessName: string;
    businessType: string;
    websiteType: string;
    outline: any;
    styles: any;
    images?: any[];
    figmaUrl?: string;
  }) {
    // Set up prefilled data structure
    this.prefilledAiData = {
      initial_description: data.description,
      enhanced_description: data.enhancedDescription,
      site_description: data.enhancedDescription,
      business_name: data.businessName,
      industry_name: data.businessType,
      business_type: data.businessType,
      website_type: data.websiteType || 'basic',
      images: data.images,
      figma_url: data.figmaUrl
    };

    // Store in localStorage
    localStorage.setItem('prefilledAiData', JSON.stringify(this.prefilledAiData));

    // Set website type
    this._commonDataService.websiteType = data.businessType;
    this._initSiteType(data.websiteType);

    // Set enhanced description
    this.builderInteractionsService.setEnhancedDescription(data.enhancedDescription);
    this.builderInteractionsService.enhancedDescriptionIsDone.next(true);

    // Prepare site info
    const siteInfo = {
      business_description: data.enhancedDescription,
      business_type: data.businessType,
      business_name: data.businessName,
      website_type: data.websiteType || 'basic',
      initial_description: data.description,
      domain_name: '',
      analysis: data.outline?.analysis || ''
    };

    if (data.images) {
      siteInfo['images'] = data.images;
    }
    if (data.figmaUrl) {
      siteInfo['figma_url'] = data.figmaUrl;
    }

    this.builderInteractionsService.siteInfo.next(siteInfo);
    this.builderInteractionsService.websiteCreationPrompt.next(data.description);


    setTimeout(() => {
      // For trigger description flow with pre-generated data, show sidebar and actions line immediately
      this.builderInteractionsService.discoverBusinessDataReady.next(true);
      // Start sidebar animations immediately when discover-business data is ready
      this._triggerService.startSidebarAnimations.next(true);
    }, 1500);

    // Handle the outline response
    if (data.outline) {
      this._handleOutlineResp(data.outline);
    }

    // Handle the styles response if provided
    if (data.styles) {
      this._handleStyleResp(data.styles);
    }
  }

  private _handleTriggerDescriptionFlow() {
    // Called when triggerDescriptionFlow @Input is provided
    const { description, industryName, websiteType, images, figmaUrl, uniqueId, enhancedDescription, businessName, businessType, outline, styles } = this.triggerDescriptionFlow;

    this.isFromTriggerDescriptionFlow = true;
    this.prefilledAi = true;
    this._commonDataService.prefilledAi = this.prefilledAi;

    // Set uniqueId if provided
    if (uniqueId) {
      this.builderHistoryService.setSessionId(uniqueId);
      this.uniqueId = uniqueId;
      this._commonDataService.uniqueId = this.uniqueId;
    }

    // Set builder steps and tour step to match onGenerate behavior in create-company-info
    this.builderHistoryService.tourStep.next(BuilderTourEnum.START);

    // For mobile auto-generate, show finalize step immediately and never show canvas
    if (this.isMobileAutoGenerate || this.isMobile) {
      this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_FINALIZE);
    } else {
      this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);
    }

    this.builderHistoryService.removeHeroSectionRespFromStorage();
    this.builderHistoryService.removeHeroSectionFromStorage();
    this.builderInteractionsService.prefilledDescription.next(description);

    // Initialize empty sections array to show canvas immediately
    this.builderInteractionsService.pageSections = [];

    // Set enhancedDescriptionIsDone to true immediately to show canvas without waiting for API
    this.builderInteractionsService.enhancedDescriptionIsDone.next(true);

    // Hide sidebar and actions line until discover-business data is ready
    this.builderInteractionsService.discoverBusinessDataReady.next(false);

    // If outline is already provided, skip API calls and directly set the data
    if (outline && enhancedDescription) {
      this._handlePreGeneratedData({
        description,
        enhancedDescription,
        businessName,
        businessType,
        websiteType,
        outline,
        styles,
        images,
        figmaUrl
      });
      return;
    }

    // Otherwise, call discover-business API like the old flow
    this._getBusinessDescData(description, industryName || null, websiteType || 'basic', images || null, figmaUrl || null);
  }

  checkPrefilledData() {
    if (localStorage.getItem('prefilledAiData')) {
      this.prefilledAi = true;
      this._commonDataService.prefilledAi = this.prefilledAi;
      this.prefilledAiData = JSON.parse(localStorage.getItem('prefilledAiData'));

      if (!this._route.snapshot.queryParams['prefilledAiData'] && !localStorage.getItem('builder_history') && (!this.isMobile || (this.vibeCodingFlow && this.isMobile))) {
        this.builderInteractionsService.prefilledDescription.next(this.prefilledAiData.initial_description);
        const initialDesc = this.prefilledAiData.initial_description;
        const industryName = this.prefilledAiData.industry_name || null;
        const websiteType = this.prefilledAiData.website_type || null;
        const images = this.prefilledAiData.images || null;
        const figma_url = this.prefilledAiData.figma_url || null;

        if (!this.user) {
          // Old flow (no user): Call discover-business API first, then generate-outline
          this._getBusinessDescData(initialDesc, industryName, websiteType, images, figma_url);
        }
        else {
          // New mobile auto-generate flow (user exists): Process directly without discover-business API
          const businessName = this.prefilledAiData.business_name || null;
          const businessType = this.prefilledAiData.business_type || null;
          this._processDescDataDirectly(initialDesc, industryName, websiteType, images, figma_url, businessName, businessType);
        }
      }
    }
    this._route.queryParams
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(params => {
        const prefilledData = params['prefilledAiData'];
        if (prefilledData) {
          localStorage.removeItem("prefilledAiData");
          this._removeSavedState();
        }
        if (!prefilledData) {
          return;
        }
        const prefilledAiDataParsed = JSON.parse(prefilledData);
        const initialDesc = prefilledAiDataParsed?.initial_description || this.prefilledAiData?.initial_description;
        const industryName = prefilledAiDataParsed?.industry_name || null;
        const websiteType = prefilledAiDataParsed?.website_type || null;
        const images = prefilledAiDataParsed?.images || null;
        const figma_url = prefilledAiDataParsed?.figma_url || null;
        this.builderInteractionsService.prefilledDescription.next(initialDesc);
        if (initialDesc) {
          const queryParamsIndex: number = this._router.url.indexOf('?');
          if (queryParamsIndex !== -1) {
            this._location.replaceState(this._router.url.substring(0, queryParamsIndex));
          }
          this.prefilledAi = true;
          this._commonDataService.prefilledAi = this.prefilledAi;
          this._setBuilderStep();
          this.builderHistoryService.removeHeroSectionRespFromStorage();
          this.builderHistoryService.removeHeroSectionFromStorage();

          if (!this.user) {
            // Old flow (no user): Call discover-business API first, then generate-outline
            this._getBusinessDescData(initialDesc, industryName, websiteType, images, figma_url);
          } else {
            // New mobile auto-generate flow (user exists): Process directly without discover-business API
            const businessName = prefilledAiDataParsed?.business_name || null;
            const businessType = prefilledAiDataParsed?.business_type || null;
            this._processDescDataDirectly(initialDesc, industryName, websiteType, images, figma_url, businessName, businessType);
          }
        }
      });
  }

  private _getBusinessDescData(initialDesc, industryName = null, websiteType = null, images = null, figma_url = null) {
    this.reCaptchaV3Service.execute(Config.googleRecaptcha, 'signup', (token) => {
      // Pass additional parameters to the generateAiBuilderDesc method
      this._builderService.generateAiBuilderDesc(initialDesc, images)
        .pipe(
          filter((response: DbResponse): boolean => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              if (response.data.enhanced_description) {
                response.data = {
                  ...response.data,
                  site_description: response.data.enhanced_description,
                  initial_description: initialDesc,
                  images: images,
                  figma_url: figma_url,
                  industry_name: response.data.business_type,
                  analysis: !this._vibeCodingFlowService.isCombinedOutline() ? '' : response.data.analysis,
                };
              }
            }
            return response;
          }),
          first()
        )
        .subscribe((response) => {
          if (response.success) {
            const getPrefilledAiDatLocal = localStorage.getItem('prefilledAiData');
            const prefilledAiDataParam = response.data || (getPrefilledAiDatLocal ? JSON.parse(getPrefilledAiDatLocal) : null);
            this._commonDataService.websiteType = prefilledAiDataParam?.business_type;
            // Set ecommerceVibeCodingVersion cookie after website type is determined
            if (prefilledAiDataParam && !this.outline) {
              this.prefilledAiData = prefilledAiDataParam;
              localStorage.setItem('prefilledAiData', JSON.stringify(this.prefilledAiData));
              this.builderInteractionsService.setEnhancedDescription(prefilledAiDataParam.site_description);

              // For trigger description flow, show sidebar and actions line after discover-business is ready
              if (this.isFromTriggerDescriptionFlow) {
                setTimeout(() => {
                  this.builderInteractionsService.discoverBusinessDataReady.next(true);
                  // Start sidebar animations immediately when discover-business data is ready
                  this._triggerService.startSidebarAnimations.next(true);
                }, 1500);
              }
              else if (!this.user) {
                this.builderInteractionsService.discoverBusinessDataReady.next(true);
              }

              this._prefilledOutlineGeneration(prefilledAiDataParam);
            }
          } else {
            if (this.isMobile) {
              this.prefilledAiData = {"business_description": initialDesc};
              localStorage.setItem('prefilledAiData', JSON.stringify(this.prefilledAiData));
              this.builderHistoryService.saveFormData(this.prefilledAiData);

            } else {
              this.prefilledAiData = {};
            }
          }
        });
    });
  }

  private _processDescDataDirectly(initialDesc, industryName = null, websiteType = null, images = null, figma_url = null, businessName = null, businessType = null) {
    // New mobile auto-generate flow: Process the description directly without calling generateAiBuilderDesc
    // Extract basic information from the description or use defaults

    // Check if siteInfo already exists from discover-business API call (from website-prompt)
    const existingSiteInfo = this.builderInteractionsService.siteInfo.value;

    // Use provided businessName or try to extract it from description, or use existing siteInfo
    const finalBusinessName = businessName || existingSiteInfo?.business_name || this._extractBusinessName(initialDesc) || '';

    // Determine website type if not provided, or use existing siteInfo
    const finalWebsiteType = websiteType || existingSiteInfo?.website_type || this._detectWebsiteType(initialDesc);

    // Use provided businessType or determine industry from description, or use existing siteInfo
    const finalIndustry = businessType || industryName || existingSiteInfo?.business_type || this._detectIndustry(initialDesc);

    // Use existing business_description if available, otherwise use initial description
    const finalBusinessDescription = existingSiteInfo?.business_description || initialDesc;

    // Determine number of pages based on website type and description complexity
    const numberOfPages = this._determineNumberOfPages(initialDesc, finalWebsiteType);

    // Create a response with the extracted/default information
    const directResponse = {
      success: true,
      data: {
        enhanced_description: finalBusinessDescription,
        business_name: finalBusinessName,
        business_type: finalIndustry,
        website_type: finalWebsiteType,
        site_description: finalBusinessDescription,
        initial_description: initialDesc,
        images: images,
        figma_url: figma_url,
        industry_name: finalIndustry,
        number_of_pages: numberOfPages,
        analysis: ''
      }
    };

    // Process the response immediately
    this._processEnhancedDescription(directResponse);
  }

  private _extractBusinessName(description: string): string {
    // Try to extract business name from common patterns
    // Pattern 1: "for [Business Name]"
    const forPattern = /for\s+([A-Z][a-zA-Z\s&'-]+)/i;
    const forMatch = description.match(forPattern);
    if (forMatch) {
      return forMatch[1].trim();
    }

    // Pattern 2: "[Business Name] is/offers/provides"
    const isPattern = /^([A-Z][a-zA-Z\s&'-]+)\s+(is|offers|provides|specializes)/i;
    const isMatch = description.match(isPattern);
    if (isMatch) {
      return isMatch[1].trim();
    }

    // Pattern 3: First capitalized phrase (up to 5 words)
    const firstCapPattern = /^([A-Z][a-zA-Z\s&'-]{0,50}?)(?:\s+(?:is|offers|provides|specializes|website|site)|\.|\,)/i;
    const firstCapMatch = description.match(firstCapPattern);
    if (firstCapMatch) {
      return firstCapMatch[1].trim();
    }

    return '';
  }

  private _detectWebsiteType(description: string): string {
    const lowerDesc = description.toLowerCase();

    // Check for ecommerce indicators
    const ecommerceKeywords = ['shop', 'store', 'buy', 'sell', 'product', 'cart', 'ecommerce', 'e-commerce', 'purchase', 'order'];
    if (ecommerceKeywords.some(keyword => lowerDesc.includes(keyword))) {
      return 'ecommerce';
    }

    return 'basic';
  }

  private _detectIndustry(description: string): string {
    const lowerDesc = description.toLowerCase();

    // Map of keywords to industries
    const industryMap = {
      'restaurant': ['restaurant', 'cafe', 'food', 'dining', 'menu', 'chef'],
      'retail': ['shop', 'store', 'boutique', 'retail'],
      'technology': ['tech', 'software', 'app', 'digital', 'development'],
      'healthcare': ['health', 'medical', 'clinic', 'doctor', 'hospital'],
      'education': ['school', 'education', 'learning', 'course', 'training'],
      'real estate': ['real estate', 'property', 'housing', 'apartment'],
      'services': ['service', 'consulting', 'agency', 'professional']
    };

    for (const [industry, keywords] of Object.entries(industryMap)) {
      if (keywords.some(keyword => lowerDesc.includes(keyword))) {
        return industry;
      }
    }

    return 'business';
  }

  private _determineNumberOfPages(description: string, websiteType: string): number {
    const wordCount = description.split(/\s+/).length;

    // Base number of pages
    let numberOfPages = 3; // Default: Home, About, Contact

    // Increase for ecommerce
    if (websiteType === 'ecommerce') {
      numberOfPages = 5; // Home, Shop, Product, Cart, Checkout
    }

    // Increase based on description complexity
    if (wordCount > 50) {
      numberOfPages += 1;
    }
    if (wordCount > 100) {
      numberOfPages += 1;
    }

    return Math.min(numberOfPages, 7); // Cap at 7 pages
  }

  private _processEnhancedDescription(response) {
    if (response.success) {
            const getPrefilledAiDatLocal = localStorage.getItem('prefilledAiData');
            const prefilledAiDataParam = response.data || (getPrefilledAiDatLocal ? JSON.parse(getPrefilledAiDatLocal) : null);
            this._commonDataService.websiteType = prefilledAiDataParam?.business_type;
            // Set ecommerceVibeCodingVersion cookie after website type is determined
            if (prefilledAiDataParam && !this.outline) {
              this.prefilledAiData = prefilledAiDataParam;
              localStorage.setItem('prefilledAiData', JSON.stringify(this.prefilledAiData));
              this.builderInteractionsService.setEnhancedDescription(prefilledAiDataParam.site_description);

              // For trigger description flow or new flow (user exists), set enhancedDescriptionIsDone to true to skip animation
              // For old flow (!user), let prefilled-company-info component control it
              if (this.isFromTriggerDescriptionFlow || this.user) {
                this.builderInteractionsService.enhancedDescriptionIsDone.next(true);
              }

              this._prefilledOutlineGeneration(prefilledAiDataParam);
            }
          } else {
            const initialDesc = response.data?.initial_description;
            if (this.isMobile) {
              this.prefilledAiData = {"business_description": initialDesc};
              localStorage.setItem('prefilledAiData', JSON.stringify(this.prefilledAiData));
              this.builderHistoryService.saveFormData(this.prefilledAiData);

            } else {
              this.prefilledAiData = {};
            }
          }
  }

  private _prefilledOutlineGeneration(prefilledAiDataParam) {
    const websiteType = this.prefilledAiData?.website_type;
    this._initSiteType(websiteType);
    this.builderInteractionsService.websiteCreationPrompt.next(prefilledAiDataParam.initial_description);
    let data = this.builderInteractionsService.transformOldFormData(prefilledAiDataParam);
    const isVibeCoding = this._vibeCodingFlowService.isVibeCodingFlow() ? 1 : 0;
    if (this.isMobile && !isVibeCoding) {
      const heroGenerationData = {
        input: data.business_description,
        industry: data.business_type,
        square: Math.random() < 0.5,
        website_type: data.website_type
      };
      this.builderInteractionsService.heroSectionData.next(heroGenerationData);
    }
    if (websiteType === 'ecommerce' && !this.vibeCodingFlow && !this.isMobile) {
      this.getEcomDataForPrefilled(data);
      return;
    } else {
      if (this.isMobile && !this.vibeCodingFlow) {
        this.builderHistoryService.saveFormData(data);
        if (this.user) {
          this.generateWebsite();
        }
        return;
      }
    }
    // For new flow (user exists), set enhancedDescriptionIsDone immediately to skip animation
    // For old flow (!user), let prefilled-company-info component control it
    if (this.user) {
      this.builderInteractionsService.enhancedDescriptionIsDone.next(true);
    }
    this.generateOutlineRequest();
  }

  private getEcomDataForPrefilled(data) {
    this._builderService
      .generateProducts(data, null, this.uniqueId)
      .pipe(filter((response: DbResponse): boolean => !!response), first())
      .subscribe((servicesResponse) => {
        if (servicesResponse.success && servicesResponse.data) {
          const generatedServices = servicesResponse.data;
          const formatedData = {
            categories: generatedServices['categories'],
            blacklisted_categories: [],
            collections: generatedServices['collections']
          };
          this.builderInteractionsService.updateProductCategories.next(formatedData);
          if (this.isMobile) {
            const generationData = {
              ecom_data: formatedData,
              'website_type': 'ecommerce',
              ...data
            };
            this.builderHistoryService.saveFormData(generationData);
            if (this.user) {
              this.generateWebsite();
            }
          } else {
            // For desktop, call generateOutlineRequest after products are generated
            if (this.user) {
              this.builderInteractionsService.enhancedDescriptionIsDone.next(true);
            }
            this.generateOutlineRequest();
          }
        }
      });
  }

  private _initSiteType(websiteType?) {
    const websiteTypeParam = websiteType || this._route.snapshot.queryParamMap.get('websiteType') || this.companyType || this.builderInteractionsService?.siteInfo?.value?.website_type || this.builderInteractionsService.getWebsiteTypeMobile();
    const type = websiteTypeParam === 'ecommerce' ? websiteTypeParam : 'basic';

    this.builderInteractionsService.saveWebsiteTypeMobile(websiteTypeParam === 'ecommerce' ? 'ecommerce' : 'basic');
    this.isEcommerce = websiteTypeParam === 'ecommerce' || this.builderInteractionsService.websiteType.value === 'ecommerce';
    this._commonDataService.secondaryPagesLimit = builderLimits[type].pagesCount;
    this._commonDataService.secondaryPagesActiveSectionsLimit = builderLimits[type].secondarySectionsCount - 2;
    this._commonDataService.homePageSectionsLimit = builderLimits[type].homeSectionsCount - 2;
    this._commonDataService.websiteType = type;
  }

  private _listenEnhance() {
    this.builderInteractionsService?.enhanceSection
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(value => {
        const requestObj = this.builderInteractionsService.getModifiedSectionStructure(this.builderInteractionsService?.pageSections, value.index);
        requestObj.page_outline.sections = this.builderInteractionsService?.removeExtraKeysFromSections(requestObj.page_outline.sections, true);
        switch (value.type) {
          case "enhance": {
            this._onEnhanceSection(requestObj);
            break;
          }
          case "description": {
            this._onEnhanceSectionDescription(requestObj);
            break;
          }
          case "title": {
            this._onEnhanceSectionTitle(requestObj);
            break;
          }
          case "new": {
            this._onGenerateSection(requestObj);
            break;
          }
        }
      });
  }

  private _onEnhanceSectionDescription(sectionObj) {
    this._enhanceSectionDescription(sectionObj);
  }

  private _onEnhanceSection(sectionObj) {
    this._enhanceSection(sectionObj);
  }

  private _onEnhanceSectionTitle(sectionObj) {
    this._enhanceSectionTitle(sectionObj);
  }

  private _onGenerateSection(sectionObj) {
    this._generateSection(sectionObj);
  }


  closeModal(): void {
    if (this._generationModalRef) {
      this._generationModalRef.close();
    }
  }

  loadFontStyles() {
    this.fontStyles = SUPPORTED_FONTS_LIST.map(font => {
      const fontStyle = `@import url('https://fonts.googleapis.com/css2?family=${font.replace(/\s+/g, '+')}:wght@400;600;700&display=swap');`;
      return this.sanitizer.bypassSecurityTrustHtml(`<style>${fontStyle}</style>`);
    });
  }

  checkSectionTitles() {
    const emptySections = [];

    // Process secondaryPages
    this.builderInteractionsService.secondaryPages.value.forEach(page => {
      page.sections.map((section) => {
        if (!section.section_title && section.section_description) {
          const requestObj = this.builderInteractionsService.getModifiedSectionStructure(page.sections, section.index);
          requestObj.page_outline.sections = this.builderInteractionsService.removeExtraKeysFromSections(requestObj.page_outline.sections, true);
          emptySections.push(this._builderService.enhanceSectionTitle(requestObj, this.domainId, this.uniqueId, false));
        }
      });
    });

    // Process pageSections
    this.builderInteractionsService.pageSections.forEach((section: BuilderGeneratedPageSectionI) => {
      if (!section.section_title && section.section_description) {
        const requestObj = this.builderInteractionsService.getModifiedSectionStructure(this.builderInteractionsService.pageSections, section.index);
        requestObj.page_outline.sections = this.builderInteractionsService.removeExtraKeysFromSections(requestObj.page_outline.sections, true);
        emptySections.push(this._builderService.enhanceSectionTitle(requestObj, this.domainId, this.uniqueId, false));
      }
    });

    if (!emptySections.length) {
      return;
    }

    // Combine and handle all requests
    combineLatest(emptySections)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(requests => requests?.every(request => !!request?.success)),
        map(requests => requests.map(request => request.data))
      )
      .subscribe((value: any[]) => {
        value.forEach((value: any) => {
          // Update secondaryPages
          this.builderInteractionsService.secondaryPages.value.map(page =>
            page.sections.map(section => {
              if (section.id === value.id) {
                section.section_title = value.section_title;
                section.title_control?.setValue(value.section_title);
                section.section_type = null;
              }
            })
          );

          // Update pageSections
          const index = this.builderInteractionsService.pageSections.findIndex(section => section.section_description === value.section_description);
          if (index !== -1) {
            this.builderInteractionsService.pageSections[index].section_title = value.section_title;
            this.builderInteractionsService.pageSections[index]?.title_control?.setValue(value.section_title);
            this.builderInteractionsService.pageSections[index].section_type = null;
          }
        });
      });
  }

  generateWebsite(): void {
    // vibeCodingFlow is now automatically updated via subscription in ngOnInit

    if (!this.user) {
      if (!this.vibeCodingFlow && !this.isMobile) {
        this.builderInteractionsService.buildLoading.next(true);
      }
      this._showSignUpComponent();
    } else {
      // Set loading state for prefilledAi flows (including section_based from dialog)
      if (this.prefilledAi && !this.vibeCodingFlow && !this.isMobile && this.builderType === 'section_based') {
        this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_FINALIZE);
        this.builderInteractionsService.generatePrefilledSiteLoading.next(true);
      }
      const storageData = this.builderHistoryService.getStateFromStorage() || this.builderHistoryService.getFormData();
      this._builderService.setGenerationFinalize(storageData, this.uniqueId)
        .pipe(
          filter(response => response && !!response)
        )
        .subscribe((response) => {
          if (response && response.success) {
            this._generateWebsite();
          }
        });
    }
  }

  public showFinalize(fromGoogleSignup, fromMobileSignup): void {
    if (fromGoogleSignup || fromMobileSignup) {
      if (this.user) {
        this.generateWebsite();
      } else {
        this.builderHistoryService.builderStep.next(BuilderStepsEnum.CHOOSE_BUSINESS_TYPE);
      }
    }
  }

  public getUser(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription),
          this._store.select(fromStore.getUserLocalStorage),
          this._store.select(fromStore.getWebsite(this.domainId))
        )))
      .subscribe(([user, userSubscription, userLocalStorage, website]) => {
        this.user = user;
        this.userSubscription = userSubscription;
        this.userLocalStorage = userLocalStorage;
        if (sessionStorage.getItem('ai_color') && this.user && !this.aiColorsSet) {
          this.aiColorsSet = true;
          this.userLocalStorage['ai_color'] = sessionStorage.getItem('ai_color');
          if (sessionStorage.getItem('ai_font')) {
            this.userLocalStorage['ai_font'] = sessionStorage.getItem('ai_font');
          }
          this._updateLocalStorage();
        }

        this.websiteInfo = website;
        this._pluginsLoaded();
      });
  }

  private _getUniqueId(): void {
    this._builderService.getBuilderUniqueId()
      .subscribe(
        (response) => {
          if (response && response.data) {
            this.builderHistoryService.setSessionId(response.data.uniqueId);
            this.uniqueId = this.builderHistoryService.getUniqueId();
            this._commonDataService.uniqueId = this.uniqueId;
            // UniqueId is now ready, proceed with flow
            this._proceedWithFlow();
          }
        });
  }

  private _proceedWithFlow(): void {
    // If triggerDescriptionFlow is provided, use it to start the flow
    if (this.triggerDescriptionFlow) {
      this._handleTriggerDescriptionFlow();
    } else {
      this.checkPrefilledData();
    }
  }

  private _initOutline() {
    const storageData = this.isSinglePageMode ?
      this.builderHistoryService.getPageFromStorage() :
      this.builderHistoryService.getStateFromStorage();
    if (storageData && (this.currentOutline || !this.popupMode)) {
      // Add analytics check
      this._setStateFromStorage(storageData);
      return;
    }
  }

  private _listenChangeInState() {
    const urlInfo = (<any>this._activeRoute.queryParams).value;
    if (urlInfo && urlInfo?.magicLink) {
      const jsonString = LZString.decompressFromEncodedURIComponent(urlInfo?.magicLink);
      const dataFromUrl = JSON.parse(jsonString);
      sessionStorage.setItem('sessionId', dataFromUrl?.uniqueId);
      this.builderHistoryService.saveStateInStorage(dataFromUrl);
    } else {
      this.builderInteractionsService.saveLocalState
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(_ => {
          if (this.isSinglePageMode) {
            this.builderHistoryService.saveSinglePageInStorage({
              ...this._getGenerationStructure(),
              active_color: this.builderInteractionsService.activeColor.value,
              generated_color: WEBSITE_CUSTOMIZATIONS.colors[0],
              tour_step: this.builderHistoryService.tourStep.value,
              siteInfo: this.builderInteractionsService.siteInfo.value,
              website_info: this.builderInteractionsService.websiteInfo,
              page_form: this._singePageService.pageForm,
              single_page: this._singePageService.page.value,
              site_data: this._singePageService.siteData,
              brandKitOptions: this.builderInteractionsService.brandKitOptions.value,
              customPostTypes: this.customPostTypes
            });
            return;
          }
          this.builderHistoryService.saveStateInStorage({
            ...this._getGenerationStructure(),
            active_color: this.builderInteractionsService.activeColor.value,
            generated_color: WEBSITE_CUSTOMIZATIONS.colors[0],
            tour_step: this.builderHistoryService.tourStep.value,
            siteInfo: this.builderInteractionsService.siteInfo.value,
            website_info: this.builderInteractionsService.websiteInfo,
            brandKitOptions: this.builderInteractionsService.brandKitOptions.value,
            customPostTypes: this.customPostTypes
          });
          this._builderTriggerService.calculateMinZoom.next();
        });
    }
  }

  private _listenSectionChange() {
    this.builderInteractionsService.sectionChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        const sectionObj: any = this.builderInteractionsService.getModifiedSectionStructure(value.sections, value.changedIndex);
        sectionObj.page_outline.sections = this.builderInteractionsService.removeExtraKeysFromSections(sectionObj.page_outline.sections);
        sectionObj.page_title = 'Home';
        sectionObj.page_type = 'home';
        this._getModifiedSectionType(sectionObj);
      });
  }

  private _listenTour() {
    this.builderHistoryService.tourStep
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(value => {
        this.tourStep = value;
        if (this.tourEnum.OUTLINE_POPUP !== value) {
          this.builderInteractionsService.saveLocalState.next();
        }
      });
  }

  private _listenBuilderNotifications() {
    this._subscribeToNotifs();
    this.builderInteractionsService.requestSubject.pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(({action, response}) => {
      if (!response) {
        return;
      }
      if (!response.success && !response.data) {
        this._showErrorNotif();
      }
      this._runRequestAction(action, response.data, this.uniqueId);
    });
  }

  private _handleStyleResp(styleResponse: PageThemeI) {
    const newColor = {
      title: 'AI suggested',
      primaryColor: styleResponse?.colors?.primary_color,
      secondaryColor: styleResponse?.colors?.secondary_color,
    };
    sessionStorage.setItem('ai_color', JSON.stringify(newColor));
    sessionStorage.setItem('ai_font', JSON.stringify(styleResponse?.fonts?.primary_font));

    const existingIndex = WEBSITE_CUSTOMIZATIONS.colors.findIndex(
      (color) => color.title === 'AI suggested'
    );

    if (existingIndex !== -1) {
      // Replace the existing 'AI suggested' color
      WEBSITE_CUSTOMIZATIONS.colors[existingIndex] = newColor;
    } else {
      // Add the new 'AI suggested' color to the beginning of the array
      WEBSITE_CUSTOMIZATIONS.colors.unshift(newColor);
    }
    this.builderInteractionsService.aiGeneratedStyle = styleResponse;
    this.builderInteractionsService.activeFont.next(styleResponse.fonts?.primary_font);
    this.builderInteractionsService.defaultColor.next(newColor);
    this.builderInteractionsService.activeColor.next(newColor);
    this.pageTheme = styleResponse;
  }

  private _handleOutlineResp(outlineResponse: BuilderGeneratedPageI | CombinedOutlineRespI) {
    // this._getPageMeta(outlineResponse);
    this._initSiteType();
    if (this._vibeCodingFlowService.isCombinedOutline()) {
      this._handleCombinedOutlineResp(outlineResponse as CombinedOutlineRespI);
      return;
    }
    outlineResponse = outlineResponse as BuilderGeneratedPageI;
    this._getSeoMetaData();
    const nextStep = BuilderTourEnum.END;
    this.builderHistoryService.tourStep.next(nextStep);
    this.outline = outlineResponse as any;
    this.builderInteractionsService.pageSections = outlineResponse.page_outline.sections.map((section, i) => {
      section.id = new Date().getTime() + i;
      section.section_type = null;
      return section;
    });
    const sectionObj: any = this.builderInteractionsService.getModifiedSectionStructure(this.builderInteractionsService.pageSections);
    sectionObj.page_outline.sections = this.builderInteractionsService.removeExtraKeysFromSections(sectionObj.page_outline.sections);
    sectionObj.page_title = 'Home';
    sectionObj.page_type = 'home';
    this._getModifiedSectionType(sectionObj, true);
    if (this.pageMetas) {
      this.outline.pages_meta = this.pageMetas;
    }
    this._checkLoad();
  }

  private _handleCombinedOutlineResp(outlineResponse: CombinedOutlineRespI) {
    const pageMetas = outlineResponse.outline
      .map((outline, index) => {
        return {
          "id": new Date().getTime() + index,
          index,
          description: outline.description,
          title: outline.title,
          page_type: outline.page_type,
          slug: outline.title.toLowerCase(),
          sections: outline.sections.map(({title_control, description_control, ...section}, i) => {
            return {
              ...section,
              "index": i,
              "section_description": section.section_description,
              "section_title": section.section_title,
              "section_type": section.section_type
            };
          }),
        };
      });
    this._setCustomPostTypes(outlineResponse.custom_post_types);
    const nextStep = BuilderTourEnum.END;
    const [homeMetas, ...secondaryMetas] = pageMetas;
    if (!secondaryMetas.length) {
      this.builderInteractionsService.secondaryPagesLoaded.next(true);
    }
    this.builderHistoryService.tourStep.next(nextStep);
    this.pageMetas = pageMetas;
    this.outline = {
      pages_meta: pageMetas, page_outline: homeMetas
    } as any;
    this.builderInteractionsService.secondaryPages.next(secondaryMetas.length ? this.builderInteractionsService.convertToSecondaryPages(secondaryMetas) : []);
    this.builderInteractionsService.pageSections = homeMetas.sections;
    this._checkLoad();
  }

  private _getPageMeta() {
    const {domain_name, initial_description, analysis, ...siteInfo} = this.builderInteractionsService.siteInfo.value;
    const data = {
      ecom_data: this.builderInteractionsService.updateProductCategories.value,
      ...siteInfo
    };
    this._builderService.aiGeneratePageMeta(data, this.uniqueId)
      .pipe(takeUntilDestroyed(this._destroyRef), filter(resp => !!resp))
      .subscribe(value => {
        if (value.success) {
          this.pageMetas = value.data.pages_meta;
          if (this.outline) {
            this.outline.pages_meta = this.pageMetas;
          }

          if (this.vibeCodingFlow && this.isMobile) {
            this._secondaryPagesService.pageMeta = this.pageMetas;
            this._secondaryPagesService.getSecondaryPages();
          }

          // Mark secondary pages as loaded for section_based flow
          this.builderInteractionsService.secondaryPagesLoaded.next(true);
          this.builderInteractionsService.loading.next(false);

          // this.builderInteractionsService.saveLocalState.next();
        }
        this._checkLoad();
      });
  }

  private _checkLoad() {
    if (this.outline && this.pageMetas.length) {
      this.builderInteractionsService.loading.next(false);
    }
  }

  private _setStateFromStorage(state: StorageSiteStateI) {
    this.outline = {pages_meta: state.pages_meta, page_outline: state.page_outline} as any;
    this.pageMetas = state.pages_meta;
    this._setCustomPostTypes(state.customPostTypes);
    this.pageTheme = {colors: state.colors, fonts: state.fonts} as any;
    this.builderInteractionsService.activeTheme.next(state.theme);
    this.builderInteractionsService.defaultColor.next(state.active_color);
    this.builderInteractionsService.activeColor.next(state.active_color);
    this.builderInteractionsService.activeFont.next(state.fonts.primary_font);
    this.builderHistoryService.tourStep.next(state.tour_step);
    this.builderInteractionsService.pageSections = state.page_outline.sections;
    this.builderInteractionsService.siteInfo.next(state.siteInfo);
    this.builderInteractionsService.updateProductCategories.next(state.ecom_data);
    this.builderInteractionsService.enhancedDescriptionIsDone.next(true);
    this.builderInteractionsService.websiteInfo = {
      website_description: state?.website_info?.website_description,
      website_keyphrase: state?.website_info?.website_keyphrase,
      website_title: state?.website_info?.website_title,
      website_design_type_level: state?.website_info?.website_design_type_level,
    };

    // Restore brand kit data if available
    if (state.brandKitOptions) {
      this.builderInteractionsService.brandKitOptions.next(state.brandKitOptions);
    }

    const [home, ...secondaryMetas] = state.pages_meta;
    this.builderInteractionsService.secondaryPages.next(this.builderInteractionsService.convertToSecondaryPages(secondaryMetas));
    const existingColorIndex = WEBSITE_CUSTOMIZATIONS.colors.findIndex(color =>
      color.primaryColor === state.generated_color.primaryColor &&
      color.secondaryColor === state.generated_color.secondaryColor);

    if (existingColorIndex === -1) { // Check if the color doesn't exist
      WEBSITE_CUSTOMIZATIONS.colors.unshift(state.generated_color);
    }

    if (this.prefilledAi) {
      this._checkLoad();
      this.builderInteractionsService.disableNextButton.next(false);
    }
    this._commonDataService.combinedOutline = this._vibeCodingFlowService.isCombinedOutline();
  }

  private _handleModifySectionResp(sectionTypes: BuilderGeneratedPageSectionI[]) {
    this.builderInteractionsService.updateSectionTypes.next(sectionTypes);
  }

  private _handleProductCategories(categories: ProductCategoriesI) {
    this.builderInteractionsService.updateProductCategories.next(categories);
  }

  private _handleEnhanceResp(enhancedSection) {
    this.builderInteractionsService.updateEnhancedSection.next(enhancedSection);
  }

  private _handleModifyColorResp(color: any) {
    this.pageTheme.colors = {...color};
  }


  private _setCustomPostTypes(value) {
    this.customPostTypes = value;
    this._builderService.customPostTypes = value;
  }

  private _handleBuildSite(resp: any) {
    this._store.dispatch(new fromStore.UpdateUserSubscription());
    const dbTesting = !!this._cookieService.get('db-testing');
    this.builderInteractionsService.buildLoading.next(false);
    if (!resp || !resp?.action_id) {
      NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
      localStorage.removeItem('showBuilderV2Finalize');
      sessionStorage.removeItem("sessionId");
      localStorage.removeItem("formData");
      localStorage.removeItem('prefilledAiData');
      localStorage.removeItem('dbDescEventSent');
      localStorage.removeItem('builderType');
      return;
    }
    this._store.dispatch(new fromStore.LoadWebsites());
    this._modalService.dismissAll();
    // this._removeSavedState();
    if (this.popupMode && !this.emailCampaign) {
      return;
    }
    const prefilledAiData = JSON.parse(localStorage.getItem('prefilledAiData'));
    localStorage.removeItem("dbShowV2Finalize");
    sessionStorage.removeItem("sessionId");
    localStorage.removeItem("showBuilderV2Finalize");
    localStorage.removeItem("formData");
    localStorage.removeItem('prefilledAiData');
    localStorage.removeItem('dbDemoCampaign');
    localStorage.removeItem('dbDescEventSent');
    localStorage.removeItem('builderType');
    this._store.dispatch(new fromStore.LoadUser());
    this._store.dispatch(new fromStore.LoadUserAvailableSubscriptions());
    this.builderHistoryService.getLocalStorage();
    const preview = `${resp.action_id.page_link}?from=onboarding&domain_id=${this.domainId || Config.demoDomainId}&client_id=${this.user?.id}&first_time&client_email=${this.user?.email}` + (dbTesting ? `&db-testing` : ``);

    this.userSubscription && this.user && Utils.isDemoUser(this.userSubscription, this.user) && this._globalEventDispatcher.dispatchEvent(GlobalEvent.showDemoPreviewIframe, {
      showIframe: false,
      iframeUrl: preview
    });

    this.showSuccessDialog(resp, dbTesting);
  }

  private _handleBuildSiteVibe() {
    this._store.dispatch(new fromStore.UpdateUserSubscription());
    this._store.dispatch(new fromStore.LoadWebsites());
    this._modalService.dismissAll();
    if (this.popupMode && !this.emailCampaign) {
      return;
    }
    const prefilledAiData = JSON.parse(localStorage.getItem('prefilledAiData'));
    const initialDescription = prefilledAiData?.initial_description || this.builderInteractionsService.siteInfo?.value?.initial_description;
    const enhancedDescription = prefilledAiData?.enhanced_description || prefilledAiData?.site_description;
    localStorage.removeItem("dbShowV2Finalize");
    sessionStorage.removeItem("sessionId");
    localStorage.removeItem("showBuilderV2Finalize");
    localStorage.removeItem("formData");
    localStorage.removeItem('prefilledAiData');
    localStorage.removeItem('dbDemoCampaign');
    localStorage.removeItem('dbDescEventSent');
    localStorage.removeItem('builderType');
    this._store
      .select(fromStore.getWebsitesLoaded)
      .pipe(
        filter((loaded: boolean): boolean => !!loaded),
        switchMap(() =>
          this._store.select(fromStore.getWebsites)
        ),
        first(websites => !!websites)
      )
      .subscribe((websites) => {

        if (websites?.length && (this.userSubscription?.freeBaseClient || this.userSubscription?.trialClient)) {
          this.website = websites[0];
          if (!this._hasWhitelisted && !!this.isOnboarding) {
            this._hostingService.whiteListIp(this.website?.id, this.website?.site_url).then(() => {
              DashboardUtils.redirectToWvcEditor(this.website, this.user, this.isMobile, false);
              this.builderInteractionsService.buildLoading.next(false);
            });
          } else {
            DashboardUtils.redirectToWvcEditor(this.website, this.user, this.isMobile, false);
            this.builderInteractionsService.buildLoading.next(false);
          }
          return;
        }

      });

  }

  private _handleAiGeneratedColors(resp: any) {
    const data = resp?.meta_data && JSON.parse(resp?.meta_data);
    const newColor = {
      title: 'AI suggested',
      primaryColor: data.primary_color,
      secondaryColor: data.secondary_color,
    };
    sessionStorage.setItem('ai_color', JSON.stringify(newColor));
    sessionStorage.setItem('ai_font', JSON.stringify(data?.fonts?.primary_font));
  }

  showSuccessDialog(resp, dbTesting) {
    const preview = `${resp.action_id.page_link}?from=onboarding&domain_id=${this.domainId || Config.demoDomainId}&client_id=${this.user?.id}&first_time&client_email=${this.user?.email}` + (dbTesting ? `&db-testing` : ``);
    if (!!this.isOnboarding) {
      this._store.dispatch(new fromStore.LoadWebsites());

      setTimeout(() => {

        if (this.isMobile) {
          this._router.navigateByUrl('/single-page-demo');
          this.openCongratsPopupMobile(preview);
        } else {
          if (Utils.isDemoUser(this.userSubscription, this.user)) {
            this._router.navigateByUrl('/websites');
            showConfetti();
            this.openCongratsPopup(preview);
          }
          if (Utils.isHostedTrialUser(this.userSubscription, this.user)) {
            const websiteUrl = `${this.website?.site_url}/?trial_hosted_flow=1${this.website?.isEcommerceWebsite ? '&ecommerce=1' : ''}`;
            const previewLink = DashboardUtils.generateWpAdminRedirectorUrl(this.website?.id, websiteUrl, this.user?.email);
            setTimeout(() => {
              window.open(previewLink, '_self');
            }, 3000);
          }
        }
      }, 1000);
    } else {
      showConfetti();
      if (this.isMobile) {
        this.openCongratsPopupMobile(preview);
      } else {
        this.openCongratsPopup(preview);
      }
    }
  }

  openCongratsPopup(preview: string) {
    this._globalEventDispatcher.dispatchEvent(GlobalEvent.showDemoPreviewIframe, {
      showIframe: true,
      iframeUrl: preview,
    });
    this._dialogService.addDialog(NotificationDialogComponent, {
      header: 'Your website is ready!',
      message: 'Successfully created with AI.',
      buttonText: 'Preview and Edit',
      buttonColor: 'black',
      hasGradientBorder: false,
      gradientBorder: 'purple-blue',
      hasAdminLink: false,
      action: 'preview',
      hasCloseButton: false,
      withBlur: true,
      showLottie: true,
      textAlign: 'left',
      mainClass: 'generation-success',
      combinedHeader: true,
      withBlackBlur: true
    }).subscribe((confirmed: string): void => {
      if (confirmed === 'preview') {
        this.builderHistoryService.setToUserLocalStorage(!this.popupMode);
        this.builderHistoryService.clearFormData();
        if (!this.isOnboarding) {
          window.open(preview, "_blank", "noopener");
        }
      }
    });
  }

  openVibeCodingCongratsPopup(previewLink) {
    const modal = this._modalService.open(HostedTrialStartedComponent, {width: 1000, height: 600, hasCloseBtn: false});
    modal.componentInstance.user = this.user;
    modal.componentInstance.subHeader = 'Your website is ready!';
    modal.componentInstance.icon = true;
    modal.componentInstance.flow = 'Section-based';
    modal.componentInstance.userSubscription = this.userSubscription;
    modal.componentInstance.website = this.website;
    modal.closed.subscribe(() => {
      window.open(previewLink, '_self');
    });
    modal.dismissed.subscribe(() => {
      window.open(previewLink, '_self');
    });
  }

  openCongratsPopupMobile(preview: string) {
    const modal = this._modalService.open(SuccessMobileDialogComponent, {});
    modal.componentInstance.header = 'Your website is ready!';
    modal.componentInstance.message = 'Successfully created with AI.';
    modal.componentInstance.buttonText = 'Preview and Edit';
    modal.closed.subscribe(() => {
      this.builderHistoryService.setToUserLocalStorage(!this.popupMode);
      localStorage.removeItem('prefilledAiData');
      this.builderHistoryService.clearFormData();
      if (!this.isOnboarding) {
        window.open(preview, "_blank", "noopener");
      } else {
        this._globalEventDispatcher.dispatchEvent(GlobalEvent.showDemoPreviewIframe, {
          showIframe: true,
          iframeUrl: preview,
        });
      }
    });
  }

  private _handleBuildSecondaryPage(resp) {
    if (!resp || !resp?.action_id) {
      NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
      return;
    }
    this._dialogService.addDialog(NotificationDialogComponent, {
      header: 'Your website is ready!',
      message: 'Successfully created with AI.',
      showSuccess: false,
      textAlign: 'notif',
      withTimer: 3000
    }).subscribe(() => {
      const url = DashboardUtils.generateWpAdminRedirectorUrl(this.domainId, this.websiteInfo?.adminUrl + `/post.php?post=${resp.action_id.page_id}&action=elementor`, this.user.email);
      this.builderHistoryService.removePageFromStorage();
      sessionStorage.removeItem("sessionId");
      window.open(url, '_self');
    });
  }

  private _handleBuildSiteComplete(resp, status?: string, domainIdFromActivity?: number) {
    this._store.dispatch(new fromStore.LoadWebsites());

    // Hide loading overlay
    this.builderInteractionsService.generatePrefilledSiteLoading.next(false);

    // Get domain_id from activity notification (same way as status), fallback to this.domainId
    const domainId = domainIdFromActivity || this.domainId;

    // Close parent dialog if in popup mode
    if (this.popupMode) {
      this.buildComplete.emit();
    }

    // Prevent showing the dialog multiple times
    if (this._buildCompleteDialogShown) {
      return;
    }
    this._buildCompleteDialogShown = true;



    // Check if generation failed
    if (status === ActivityStatus.FAILED) {
      if (domainId) {
        if (Utils.isDemoUser(this.userSubscription, this.user)) {
          this._router.navigate(['/single-page-demo']);
        }
        else {
          this._router.navigate(['/websites', domainId, 'main']);
        }
      }
      else {
        this._dialogService.addDialog(NotificationDialogComponent, {
          header: 'Something went wrong',
          message: 'Your website was created, but we couldn\'t complete the generation. Please navigate to the newly created website and try again.',
          buttonText: 'Got it',
          buttonColor: 'black',
          showCancelButton: false,
          hasCloseButton: false,
          action: 'close',
          showLottie: false,
          buttonSize: '116px',
          cancelBtnClass: 'ds-btn-outline m short',
          submitBtnClass: 'ds-btn-s m',
          textAlign: 'left',
          buttonAlign: 'right'
        }).subscribe(() => {
          if (domainId) {
            if (Utils.isDemoUser(this.userSubscription, this.user)) {
              this._router.navigate(['/single-page-demo']);
            }
            else {
              this._router.navigate([`websites/${domainId}/main`]);
            }
          }
        });
        return;
      }
    }
    if (Utils.isDemoUser(this.userSubscription, this.user)) {
      this._router.navigate(['/single-page-demo']);
    }
    else {
      // Show congratulations dialog for success
      this._store.select(fromStore.getWebsite(domainId))
          .pipe(
              filter(website => !!website),
              first()
          )
          .subscribe(website => {
            this._hostingService.whiteListIp(website?.id, website?.site_url).then(() => {
              this.builderInteractionsService.buildLoading.next(false);
            });
          });
      this._dialogService.addDialog(NotificationDialogComponent, {
        header: 'Congratulations!',
        message: TextUtils.getText('text_yourAigeneratedWebsiteIsReadyToCustomize'),
        buttonText: TextUtils.getText('text_previewEdit'),
        buttonColor: 'black',
        showCancelButton: true,
        hasCloseButton: false,
        action: 'preview',
        showLottie: false,
        cancelBtnClass: 'ds-btn-outline m' +  (window.innerWidth > 991 ? ' short' : ''),
        submitBtnClass: window.innerWidth > 991 ? 'ds-btn-s m' : 'm',
        textAlign: 'left',
        buttonAlign: 'right'
      }).subscribe((confirmed: string): void => {
        if (confirmed === 'preview' && domainId) {
          // Fetch website from store and navigate to editor
          this._store.select(fromStore.getWebsite(domainId))
            .pipe(
              filter(website => !!website),
              first()
            )
            .subscribe(website => {
              this._hostingService.whiteListIp(website?.id, website?.site_url).then(() => {
                this.builderInteractionsService.buildLoading.next(false);
                const redirectPath = '?edit.php?post_type=page&from=tenweb_dashboard&open=homepage';
                const redirectUrl = website?.adminUrl + redirectPath;
                const previewLink = DashboardUtils.generateWpAdminRedirectorUrl(
                    website.id,
                    redirectUrl,
                    this.user?.email
                );
                window.open(window.location.origin + '/' + previewLink, '_blank', 'noopener');
                this._router.navigate([`websites/${domainId}/main`]);
              });
            });
        }
      });
    }
  }

  private _removeSavedState() {
    this.builderHistoryService.resetSubjects();
    this.builderInteractionsService.resetSubjects();
    this.builderHistoryService.removeStateFromStorage();
  }

  private _showErrorNotif() {
    NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
  }

  private _getModifiedSectionType(data, initial = false) {
    this._builderService.aiModifySectionTypes(data, this.domainId, this.uniqueId, true, initial).subscribe();
  }

  private _enhanceSection(data) {
    this._builderService.enhanceSection(data, this.domainId, this.uniqueId).subscribe();
  }

  private _enhanceSectionTitle(data) {
    this._builderService.enhanceSectionTitle(data, this.domainId, this.uniqueId).subscribe();
  }

  private _enhanceSectionDescription(data) {
    this._builderService.enhanceSectionDescription(data, this.uniqueId).subscribe();
  }

  private _generateSection(data) {
    this._builderService.generateSection(data, this.domainId, this.uniqueId).subscribe();
  }

  private _getGenerationStructure(dataFromUrl?): SiteGenerationI {
    const sections = this.builderInteractionsService.removeFormControls(this.builderInteractionsService?.pageSections
      .filter(section => section.section_title || section.section_description)
      .map(({loading, is_tempo, ...section}, index) => {
        return {...section, index};
      }));
    const activeColor = dataFromUrl ? dataFromUrl?.active_color : this.builderInteractionsService.activeColor.value;
    const colors = dataFromUrl ? dataFromUrl?.colors : {
      primary_color: activeColor.primaryColor,
      secondary_color: activeColor.secondaryColor,
    };
    const siteInfo = dataFromUrl ? dataFromUrl : this.builderInteractionsService.siteInfo.value;
    const websiteInfo = dataFromUrl ? dataFromUrl : this.builderInteractionsService.websiteInfo;
    const categories = dataFromUrl?.ecom_data || this.builderInteractionsService.updateProductCategories.value;
    const getDomainInfo = localStorage.getItem('dbOnboardingDomainInfo');
    const vibeCodingVersion = localStorage.getItem('builderType');
    const productId = localStorage.getItem('product_id') || (vibeCodingVersion ? Config.vibeCodingPluginId : Config.builder2HostedPluginId);
    const imageModel = 'default';
    const trialHostedFlow = this.website ? 0 : 1;
    const additional = !this.popupMode || this.demoUser ? {ai_type: 'ai_builder_demo', domain_name: getDomainInfo} : {};

    const fromBrandKit = this.builderInteractionsService.brandKitOptions.value;

    return {
      builder_type: 'section_based',
      business_type: siteInfo?.business_type,
      business_name: siteInfo?.business_name,
      business_description: siteInfo?.business_description,
      website_title: websiteInfo?.website_title,
      website_description: websiteInfo?.website_description,
      website_keyphrase: websiteInfo?.website_keyphrase,
      website_design_type_level: websiteInfo?.website_design_type_level,
      page_outline: {sections},
      fonts: dataFromUrl ? dataFromUrl?.fonts : {primary_font: this.builderInteractionsService.activeFont.value},
      colors: activeColor.title === "Ai suggested" ? this.pageTheme.colors : colors,
      theme: ((dataFromUrl ? dataFromUrl?.theme?.toLowerCase() : this.builderInteractionsService.activeTheme.value?.toLowerCase()) ?? 'light').replace(/^text_/, ''),
      pages_meta: this._getSecondaryPagesForGeneration(dataFromUrl),
      post_status: 'publish',
      ecom_data: categories,
      website_type: this.isEcommerce ? 'ecommerce' : 'basic',
      image_model: imageModel,
      trial_hosted_flow: trialHostedFlow,
      ...fromBrandKit,
      ...additional,
    } as SiteGenerationI;

  }

  private _getVibeGenerationStructure(dataFromUrl?): Observable<SiteGenerationVibeCodingI> {
    return this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUser)),
        first(user => !!user),
        map((user: User) => {
          this.user = user;
          const activeColor = dataFromUrl ? dataFromUrl?.active_color : this.builderInteractionsService.activeColor.value;
          const siteInfo = dataFromUrl ? dataFromUrl : this.builderInteractionsService.siteInfo.value;
          const categories = this.builderInteractionsService.updateProductCategories.value;
          const getDomainInfo = localStorage.getItem('dbOnboardingDomainInfo');
          const vibeCodingVersion = localStorage.getItem('builderType');
          const productId = localStorage.getItem('product_id') || (vibeCodingVersion ? Config.vibeCodingPluginId : Config.builder2HostedPluginId);
          const imageModel = 'default';
          const additional = !this.popupMode || this.demoUser ? {
            ai_type: 'ai_builder_demo',
            domain_name: getDomainInfo
          } : {};
          const data = {
            unique_id: this.uniqueId,
            client_id: +this.user?.id,
            workspace_id: +localStorage.getItem('dbCurrentWorkspaceId'),
            pair_uuid: new Date().getTime().toString(),
            wp_user_id: '1',
            business_type: siteInfo?.business_type,
            business_name: siteInfo?.business_name,
            business_description: siteInfo?.business_description,
            user_prompt: siteInfo?.siteInfo?.initial_description || dataFromUrl?.siteInfo?.initial_description || siteInfo?.initial_description,
            ecom_data: categories,
            website_type: this.isEcommerce ? 'ecommerce' : 'basic',
            trial_hosted_flow: true,
            generate_images: true,
            image_files: siteInfo?.siteInfo?.images ? siteInfo?.siteInfo?.images : siteInfo?.images || undefined,
            outline: this._getCombinedSectionsAndPages(),
            custom_post_types: this.customPostTypes,
            figma_url: siteInfo?.siteInfo?.figma_url || siteInfo?.figma_url || undefined,
          };
          if (this.website) {
            data['domain_id'] = this.website?.id;
            data['site_url'] = this.website?.site_url;
          }
          if (siteInfo?.analysis || siteInfo?.siteInfo?.analysis || this.builderInteractionsService.siteInfo.value?.analysis) {
            data['analysis'] = siteInfo.analysis || siteInfo?.siteInfo?.analysis || this.builderInteractionsService.siteInfo.value?.analysis;
          }
          return data;
        }),
      );
  }

  private _getMobileGenerationData(dataFromUrl?): SiteGenerationMobileI {
    const siteInfo = dataFromUrl ? dataFromUrl : this.builderHistoryService.getFormData();
    const getDomainInfo = siteInfo?.mobileDomainInfo || localStorage.getItem('dbOnboardingDomainInfo');
    const additional = !this.popupMode || this.demoUser ? {ai_type: 'ai_builder_demo', domain_name: getDomainInfo} : {};
    const vibeCodingVersion = localStorage.getItem('builderType');
    const productId = localStorage.getItem('product_id') || (vibeCodingVersion ? Config.vibeCodingPluginId : Config.builder2HostedPluginId);
    const imageModel = 'default';
    const trialHostedFlow = this.website ? 0 : 1;
    const fromBrandKit = this.builderInteractionsService.brandKitOptions.value;
    return {
      image_model: imageModel,
      trial_hosted_flow: trialHostedFlow,
      builder_type: 'section_based',
      business_type: siteInfo?.oldFormData?.business_type || siteInfo?.business_type,
      business_name: siteInfo?.siteInfo?.business_name || siteInfo?.business_name,
      business_description: siteInfo?.siteInfo?.business_description || siteInfo?.business_description,
      ecom_data: siteInfo?.siteInfo?.ecom_data || siteInfo?.ecom_data,
      website_type: siteInfo?.website_type || (this.isEcommerce ? 'ecommerce' : 'basic'),
      ...additional,
    } as SiteGenerationMobileI;
  }

  private _getSecondaryPagesForGeneration(dataFromUrl) {
    let pages_meta: any[];
    if (dataFromUrl && dataFromUrl?.pages_meta && !!dataFromUrl?.pages_meta?.at(0)) {
      pages_meta = dataFromUrl?.pages_meta;
    } else {
      pages_meta = this._getSecondaryPagesFromLocal();
    }

    if (!pages_meta.at(0)) {
      pages_meta = [];
    }

    return pages_meta;
  }

  private _getSecondaryPagesFromLocal() {
    let pagesArray = [];

    if (this.outline?.pages_meta?.at(0)) {
      pagesArray.push(this.outline?.pages_meta[0]);
    }

    const secondaryPagesArray = this.builderInteractionsService.getPagesMeta(false);

    if (secondaryPagesArray && !!secondaryPagesArray.length && secondaryPagesArray.at(0)) {
      pagesArray = [...pagesArray, ...secondaryPagesArray];
    }

    return pagesArray;
  }

  private _getCombinedSectionsAndPages() {
    const result = [];

    // Add main page (Home) sections
    const mainPageSections = this.builderInteractionsService.removeFormControls(this.builderInteractionsService?.pageSections
      .filter(section => section.section_title || section.section_description)
      .map(({loading, is_tempo, description_control, title_control, ...section}, index) => {
        return {...section, index};
      }));

    if (mainPageSections.length > 0) {
      result.push({
        title: 'Home',
        is_home: true,
        description: this.builderInteractionsService.websiteInfo?.website_description || 'The homepage provides a high-level overview of the app, showcasing its key features and benefits.',
        sections: mainPageSections.map(({id, index, description_control, title_control, ...section}) => ({
          ...section,
          section_title: section.section_title,
          section_description: section.section_description,
          section_type: section.section_type
        }))
      });
    }

    // Add secondary pages
    const secondaryPages = this.vibeCodingFlow ? [] : ([...(this.builderInteractionsService.secondaryPages.value || [])]);
    secondaryPages.forEach(pageWithSections => {
      if (pageWithSections.page?.title && pageWithSections.sections) {
        result.push({
          title: pageWithSections.page.title,
          description: pageWithSections.page.description || `This page provides information about ${pageWithSections.page.title}.`,
          sections: pageWithSections.sections.map(({id, index, description_control, title_control, ...section}) => ({
            ...section,
            section_title: section['section_title'],
            section_description: section['section_description'],
            section_type: section['section_type']
          }))
        });
      }
    });

    return result;
  }

  private async _generateWebsite(): Promise<void> {
    // Prevent duplicate generation calls
    if (this.generationStarted) {
      return;
    }
    this._cdr.markForCheck();
    this.generationStarted = true;
    const vibeCodingFlow = this._vibeCodingFlowService.isVibeCodingFlow() ? 1 : 0;
    if (!this.prefilledAi) {
      this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_FINALIZE);
    }
    if (this._commonDataService.websiteType === 'ecommerce' && this.popupMode && this.websiteInfo) {
      await this._builderTriggerService.installEcom(this.websiteInfo.id, this.websiteInfo, !!vibeCodingFlow);
    }
    if (!!this.popupMode && !this.isMobile) {
      if (!vibeCodingFlow) {
        // Call configureWvc for section-based regeneration when website exists
        if (this.website || this.websiteInfo) {
          const websiteType = this.isEcommerce || this.websiteInfo?.websiteType === 'ecommerce' || this.website?.websiteType === 'ecommerce'  ? 'ecommerce' : 'basic';
          this._builderService.configureWvc(this.domainId, 'section_based', websiteType).pipe(
            filter((response: DbResponse) => !!response),
            first()
          ).subscribe((response: DbResponse) => {
            if (response.success) {
              this._builderService.aiGenerateSite(
                this._getGenerationStructure(),
                this.domainId,
                this.uniqueId).subscribe();
            } else {
              console.error('configureWvc failed:', response);
              // Proceed anyway to not block the user
              this._builderService.aiGenerateSite(
                this._getGenerationStructure(),
                this.domainId,
                this.uniqueId).subscribe();
            }
          });
        } else {
          this._builderService.aiGenerateSite(
            this._getGenerationStructure(),
            this.domainId,
            this.uniqueId);
        }
      } else {
        this._getVibeGenerationStructure().subscribe(vibeData => {
          this._builderService.vibeCodingCreate(vibeData, this.domainId).subscribe();
          localStorage.removeItem("builder_history");
          setTimeout(() => {
            const websiteUrl = `${this.website?.adminUrl}admin.php?page=wvc-editor`;
            const previewLink = DashboardUtils.generateWpAdminRedirectorUrl(this.website?.id, websiteUrl, this.user?.email);
            window.location.replace(previewLink);
          }, 3000);
        });
      }
    } else if ((this.isMobile && !vibeCodingFlow) || this.shortFlow) {
      this._getGenerationData().subscribe((generationData) => {
        // Call configureWvc for section-based regeneration when website exists
        if (this.website || this.websiteInfo) {
          const websiteType = this.isEcommerce || this.websiteInfo?.websiteType === 'ecommerce' || this.website?.websiteType === 'ecommerce'  ? 'ecommerce' : 'basic';
          this._builderService.configureWvc(this.domainId, 'section_based', websiteType).pipe(
            filter((response: DbResponse) => !!response),
            first()
          ).subscribe((response: DbResponse) => {
            if (response.success) {
              this._builderService.aiGenerateSiteMobile(
                this._getMobileGenerationData(),
                this.domainId,
                this.uniqueId).subscribe();
            } else {
              console.error('configureWvc failed:', response);
              // Proceed anyway to not block the user
              this._builderService.aiGenerateSiteMobile(
                this._getMobileGenerationData(),
                this.domainId,
                this.uniqueId).subscribe();
            }
          });
        } else {
          this._builderService.aiGenerateSiteMobile(
            this._getMobileGenerationData(),
            this.domainId,
            this.uniqueId).subscribe();
        }
      });
    } else {
      this._getGenerationData().subscribe((generationData) => {
        if (!vibeCodingFlow) {
          // Call configureWvc for section-based regeneration when website exists
          if (this.website || this.websiteInfo) {
            const websiteType = this.isEcommerce || this.websiteInfo?.websiteType === 'ecommerce' || this.website?.websiteType === 'ecommerce'  ? 'ecommerce' : 'basic';
            this._builderService.configureWvc(this.domainId, 'section_based', websiteType).pipe(
              filter((response: DbResponse) => !!response),
              first()
            ).subscribe((response: DbResponse) => {
              if (response.success) {
                this._builderService.aiGenerateSite(
                  this._getGenerationStructure(generationData),
                  this.domainId,
                  this.uniqueId
                ).subscribe();
              } else {
                console.error('configureWvc failed:', response);
                // Proceed anyway to not block the user
                this._builderService.aiGenerateSite(
                  this._getGenerationStructure(generationData),
                  this.domainId,
                  this.uniqueId
                ).subscribe();
              }
            });
          } else {
            this._builderService.aiGenerateSite(
              this._getGenerationStructure(generationData),
              this.domainId,
              this.uniqueId
            ).subscribe();
          }
        } else {
          if (this.isMobile) {
            if (this.builderInteractionsService.secondaryPagesLoaded.value) {
              this._getVibeGenerationStructure(generationData).subscribe(vibeData => {
                this._builderService.vibeCodingCreate(vibeData).subscribe();
              });
            } else {
              this.builderInteractionsService.secondaryPagesLoaded
                .pipe(takeUntilDestroyed(this._destroyRef)).subscribe(value => {
                if (value) {
                  this._getVibeGenerationStructure(generationData).subscribe(vibeData => {
                    this._builderService.vibeCodingCreate(vibeData).subscribe();
                  });
                }
              });
            }
            return;
          }
          this._getVibeGenerationStructure(generationData).subscribe(vibeData => {
            this._builderService.vibeCodingCreate(vibeData).subscribe();
          });
        }
      });
    }
  }

  private _getGenerationData() {
    return this._builderService.getGenerationFinalize('finalise_generation', this.uniqueId).pipe(
      filter(response => !!response),
      first(),
      map((response) => {
        if (response && response.success) {
          return JSON.parse(response.data);
        }
        NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
      }));
  }

  private _showSignUpComponent(): void {
    const storageData = this.builderHistoryService.getStateFromStorage();
    this.storageData = storageData;
    this._builderService.setGenerationFinalize(storageData, this.uniqueId)
      .pipe(
        filter(response => response && !!response),
        first()
      )
      .subscribe((response) => {
        if (response && response.success) {
          const options = {
            hasCloseBtn: !this.isMobile,
            overflowHidden: false,
          };
          if (!this.isMobile) {
            options['width'] = 900;
            options['height'] = 500;
          }
          if (this.prefilledAi) {
            this.showSignupDialogPrefilled(storageData);
            return;
          } else {
            this.showGeneralSignup(options, storageData);
          }
        }
      });
  }

  private showSignupDialogPrefilled(storageData): void {
    if (this.popupOpened) {
      return; // Prevent opening if already open
    }

    this.popupOpened = true;
    const options = {
      hasCloseBtn: false,
      hasCloseSharpBtn: false,
      overflowHidden: false,
      height: this.campaignInfo ? 250 : (!!this.vibeCodingFlow ? 376 : 358),
      width: 460,
      backdrop: "static" as "static"
    };

    if (this.isMobile) {
      options['fixMobile'] = true;
      options['height'] = 342;
      options['position'] = 'dynamic';
    }

    const modal = this._modalService.open(SignupShortFlowComponent, options);
    modal.componentInstance.textCenter = true;
    modal.componentInstance.campaignMode = !!localStorage.getItem('dbDemoCampaign');
    modal.componentInstance.hideSubTitle = true;
    modal.componentInstance.prefilledAi = true;
    modal.componentInstance.hideDialogClose = !this.vibeCodingFlow;
    modal.componentInstance.title = this.campaignInfo ? `Sign in to view & customize your<br/> website with ${this.campaignInfo?.couponPercent}% off.` : (this.vibeCodingFlow ? 'Create your free account to<br/> generate and customize your<br/> website.' : 'Create your free account to view<br/> and customize your website.');
    modal.componentInstance.subTitle = 'websites generated with 10Web';
    modal.componentInstance.v2Data = storageData;
    modal.closed.subscribe((result) => {
      if (result === 'finalize') {
        this.builderInteractionsService.buildLoading.next(true);
        this.builderInteractionsService.generatePrefilledSiteLoading.next(true);
        this._subscribeToNotifs();
        this._generateWebsite();
      }
    });
    modal.dismissed.subscribe(() => {
      this.builderInteractionsService.buildLoading.next(false);
      this._sendGaEvent('pop-up closed');
      this.popupOpened = false;
    });
  }

  private showGeneralSignup(options, storageData): void {
    const modal = this._modalService.open(CreateAiAssistantSignUpComponent, options);
    modal.componentInstance.title = TextUtils.getText('text_signUpTitleStartAi');
    modal.componentInstance.desc = TextUtils.getText('text_signUpDescStartAi');
    modal.componentInstance.steps = this.aiSteps;
    modal.componentInstance.v2Data = storageData;
    modal.componentInstance.uniqueId = this.uniqueId;
    modal.closed.subscribe((result) => {
      if (result === 'finalize') {
        this._subscribeToNotifs();
        this._generateWebsite();
      }
    });
    modal.dismissed.subscribe(() => {
      this.builderInteractionsService.buildLoading.next(false);
      this._sendGaEvent('pop-up closed');
    });
  }

  private _subscribeToNotifs() {
    if (this._alreadySubscribedToNotifs) {
      return;
    }
    this._alreadySubscribedToNotifs = true;
    this._activityNotificationService.subscribeToActivities(true).pipe(
      takeUntilDestroyed(this._destroyRef),
      // Handle "Colors" notifications separately
      tap((data) => {
        if (data?.['message'] === "Colors") {
          this._handleAiGeneratedColors(data);
        }
      }),
      // Only pass through relevant actions
      filter((data: any) => {
        if (!data || data.progress !== 100) return false;
        const vibeCodingFlow = this._vibeCodingFlowService.isVibeCodingFlow() ? 1 : 0;

        // Build site complete or failed - show dialog
        const isBuildSiteComplete =
          (data.action === ActivityAction.BUILDER_BUILD_SITE_OUTLINE || data.action === ActivityAction.INSTALL_HOSTED_TEMPLATE) &&
          (data.status === ActivityStatus.DONE || data.status === ActivityStatus.FAILED);

        const isOutlineOrSecondary =
          [ActivityAction.BUILDER_BUILD_SITE_DESC, ActivityAction.BUILDER_SECONDARY_PAGE].includes(data.action);

        const isCreateWebsite =
          data.action === ActivityAction.CREATE_WEBSITE && (vibeCodingFlow > 0);

        return isBuildSiteComplete || isOutlineOrSecondary || isCreateWebsite;
      }),
      // Branch processing depending on action
      mergeMap((data: any) => {
        if (data.action === ActivityAction.CREATE_WEBSITE && data.status === ActivityStatus.DONE) {
          // Already has the data you need
          // Get status and domain_id directly from activity data (same way)
          return of({action: ActivityAction.CREATE_WEBSITE, response: {data}, status: data.status, domainId: data.domain_id});
        }
        if (data.action === ActivityAction.INSTALL_HOSTED_TEMPLATE) {
          // For INSTALL_HOSTED_TEMPLATE, activity data already has everything we need - don't call API
          return of({action: ActivityAction.INSTALL_HOSTED_TEMPLATE, response: {data: data}, status: data.status, domainId: data.domain_id});
        }
        if (data.action === ActivityAction.BUILDER_BUILD_SITE_OUTLINE || data.action === ActivityAction.BUILDER_BUILD_SITE_DESC) {
          // Pass status and domain_id from activity data (data.domain_id from activity notification)
          return this._builderService.getBuilderActionData(data.action, this.domainId, this.uniqueId).pipe(
            map(result => ({...result, status: data.status, domainId: data.domain_id}))
          );
        }
        // Needs fetch from builder service
        return this._builderService.getBuilderActionData(data.action, this.domainId, this.uniqueId).pipe(
          map(result => ({...result, status: undefined, domainId: data.domain_id}))
        );
      })
    ).subscribe(({action, response, status, domainId}: any) => {
      if (!response) return;
      // Pass status and domainId to handler for BUILDER_BUILD_SITE_OUTLINE, BUILDER_BUILD_SITE_DESC and INSTALL_HOSTED_TEMPLATE
      if (action === ActivityAction.BUILDER_BUILD_SITE_OUTLINE || action === ActivityAction.BUILDER_BUILD_SITE_DESC || action === ActivityAction.INSTALL_HOSTED_TEMPLATE) {
        this._runRequestAction(action, response.data, status, domainId);
      } else {
        this._runRequestAction(action, response.data, this.uniqueId);
      }
    });
  }

  // Guard against actions that have no registered handler - throwing here would tear down
  // the notifications subscription for good (_alreadySubscribedToNotifs stays true)
  private _runRequestAction(action: string, ...args: any[]): void {
    const handler = this.requestActions[action];
    if (typeof handler !== 'function') {
      console.warn('[builder-v2] no handler registered for activity action:', action);
      return;
    }
    handler(...args);
  }

  private _setBuilderStep() {
    // For mobile auto-generate, step is already set to SHOW_FINALIZE, skip this logic
    if (this.isMobileAutoGenerate || this.isMobile) {
      return;
    }

    const data = localStorage.getItem('prefilledAiData');
    const parsedData = JSON.parse(data);
    const vibeCodingFlow = this._vibeCodingFlowService.isVibeCodingFlow();
    if (localStorage.getItem('showBuilderV2Finalize') || window.location.href.indexOf("showFinalize") > -1) {
      this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_FINALIZE);
    } else if (parsedData?.heroGenerationData && this.user) {
      this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_HERO);
      this.builderInteractionsService.generatePrefilledSiteLoading.next(true);
    } else if (this.pageTheme && !(vibeCodingFlow) && !this.fromVibeCoding) {
      this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_DESIGN);
    } else if (this.outline) {
      this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);
    } else if (this.prefilledAi || !!localStorage.getItem('prefilledAiData')) {
      if (this.isMobile) {
        this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_HERO);
        return;
      }
      this.builderHistoryService.builderStep.next(BuilderStepsEnum.FILL_COMPANY_INFO);
    } else if (this.fromVibeCoding) {
      this.builderHistoryService.builderStep.next(BuilderStepsEnum.FILL_COMPANY_INFO);
    } else {
      if (this.fromWebsiteCreationPrompt) {
        this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);
      }
      else {
        this.builderHistoryService.builderStep.next(BuilderStepsEnum.CHOOSE_BUSINESS_TYPE);
      }
    }
  }

  ngOnDestroy() {
    this.closeModal();
    if (this.popupMode && !this.demoUser) {
      this._removeSavedState();
    }
    this._triggerService.resetTriggers();
    !this.isMobile && sessionStorage.removeItem("sessionId");
    !this.isMobile && localStorage.removeItem("prefilledAiData");
    !this.isMobile && localStorage.removeItem("builder_history");
    !this.isMobile && localStorage.removeItem("dbDemoCampaign");
    !this.isMobile && localStorage.removeItem("builderType");
    // Reset component state flags to ensure clean state on next open
    this.isFromTriggerDescriptionFlow = false;
    this.prefilledAi = false;
    this._commonDataService.prefilledAi = false;
  }

  private _sendGaEvent(label): void {
    const analyticsData = {
      eventCategory: 'Onboarding',
      eventAction: 'Sign-up form',
      eventLabel: label,
      uniqueId: this.uniqueId
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCoreWithoutUserData(analyticsData);
  }

  headerBack() {
    const backAction = this.builderInteractionsService.getBackAction();
    if (backAction) {
      backAction();
    } else {
      history.back();
    }
  }


  private _updateLocalStorage(): void {
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(
        first(response => !!response, null),
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
        }
      });
  }
}
